import { supabase } from "@/lib/supabase";

/**
 * 강스터디 관리자 쓰기 — Edge Function kangstudy-admin 경유.
 *
 * auction_items / awarded_items 는 RLS 로 쓰기가 service_role 전용이라
 * 클라이언트가 supabase.from(...).insert() 를 직접 부르면 조용히 0행이 된다
 * (PostgREST 는 RLS 로 걸러진 쓰기에 에러 대신 204 를 준다 — "저장됐다"로
 * 보이고 실제로는 아무것도 안 들어간다).
 *
 * 그래서 admin 화면은 반드시 이 헬퍼를 쓴다. 권한 판정은 함수 쪽 env
 * ADMIN_USER_IDS 가 정본이다.
 */
export type AdminTable = "kangstudy_auction_items" | "kangstudy_awarded_items" | "kangstudy_materials";

async function call(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke("kangstudy-admin", { body });

  if (error) {
    // FunctionsHttpError 는 본문에 우리 메시지를 담고 있다 — 그걸 살려서 보여준다.
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const payload = await ctx.json();
        if (payload?.error) throw new Error(payload.error);
      } catch (parsed) {
        if (parsed instanceof Error && parsed.message) throw parsed;
      }
    }
    throw new Error(error.message || "관리자 요청에 실패했습니다.");
  }

  const payload = (data ?? {}) as Record<string, unknown>;
  if (payload.error) throw new Error(String(payload.error));
  return payload;
}

export async function adminInsert(
  table: AdminTable,
  rows: Record<string, unknown>[],
): Promise<Record<string, unknown>[]> {
  if (rows.length === 0) return [];
  const res = await call({ table, op: "insert", rows });
  return (res.rows as Record<string, unknown>[]) ?? [];
}

/**
 * 자료실 파일 업로드 — 파일 본문은 Edge Function 을 통과하지 않는다.
 * 서명 업로드 토큰만 받아 스토리지로 직접 올린다(요청 본문 한도·base64 팽창 회피).
 */
export async function adminUploadFile(
  bucket: "kangstudy_materials",
  path: string,
  file: File,
): Promise<string> {
  const res = await call({ op: "signed_upload", bucket, path });
  const token = String(res.token);
  const signedPath = String(res.path);

  const { error } = await supabase.storage.from(bucket).uploadToSignedUrl(signedPath, token, file);
  if (error) throw new Error(`파일 업로드 실패: ${error.message}`);

  return String(res.publicUrl);
}

export async function adminUpdate(
  table: AdminTable,
  id: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await call({ table, op: "update", id, payload });
}

/** id 는 테이블마다 uuid(경매·낙찰) 이거나 bigint(자료실) 다 — 원래 타입 그대로 넘긴다. */
export async function adminDelete(table: AdminTable, ids: (string | number)[]): Promise<void> {
  if (ids.length === 0) return;
  await call({ table, op: "delete", ids });
}

/** 폼 제출함 한 건 — kangstudy_form_submissions 행(고객 PII, 관리자만 조회 가능). */
export interface FormSubmission {
  id: number;
  form_type: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  payload: Record<string, string>;
  created_at: string;
}

export async function adminListSubmissions(opts: {
  formType?: string;
  limit?: number;
  offset?: number;
}): Promise<{ rows: FormSubmission[]; total: number }> {
  const res = await call({
    op: "list_submissions",
    form_type: opts.formType,
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
  });
  return {
    rows: (res.rows as FormSubmission[]) ?? [],
    total: Number(res.total ?? 0),
  };
}
